/**
 * Gift-wrapping cart toggle.
 *
 * there's no pub/sub convention here, just a single global `CartDrawer`
 * instance (`window.cart`) that re-fetches the cart items/totals after
 * every mutation. So instead of dispatching a `cart:updated` event that
 * nothing listens for, this calls `window.cart.getCart()` directly after
 * every successful update — which works identically whether this markup
 * is in the cart drawer or on the /cart page, since the same CartDrawer
 * instance manages whichever one is present on the page.
 *
 * Markup contract (see snippets/gift-wrapping.liquid):
 *
 *   <gift-wrap-toggle
 *     data-variant-id="{{ variant.id }}"
 *     data-qty-in-cart="{{ qty }}"
 *     data-other-items="{{ other_items_in_cart }}"
 *     data-mode="per_item|per_order"
 *     data-attribute-key="{{ gift_wrap_attribute }}"
 *     data-note-attribute-key="{{ gift_wrap_note_attribute }}"
 *   >
 *     <input type="checkbox" name="attributes[...]" ...>
 *     <label>...</label>
 *     <gift-wrap-note>
 *       <textarea name="attributes[...]"></textarea>
 *     </gift-wrap-note>
 *   </gift-wrap-toggle>
 *
 * This element lives in the cart's foot/totals area, not inside
 * `[data-items-holder]` — the only part of the DOM `CartDrawer` actually
 * replaces on cart mutations (see assets/theme.js `build()`). So the
 * `init()` call for this element never repeats; its `data-other-items`/
 * `data-qty-in-cart` are a one-time snapshot from whichever page load
 * rendered them, and go stale the moment the customer adds/removes
 * anything elsewhere. `add()` therefore re-reads `/cart.js` live right
 * before computing the target quantity instead of trusting that
 * snapshot — see `currentOtherItemsCount()`.
 */

const CART_UPDATE_URL = '/cart/update.js';

const debounce = (fn, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), wait);
  };
};

const refreshCart = () => {
  if (window.cart && typeof window.cart.getCart === 'function') {
    window.cart.getCart();
  }
};

const initGiftWrapNote = (noteEl, noteAttributeKey) => {
  const textarea = noteEl.querySelector('textarea');
  const errorEl = noteEl.querySelector('[data-gift-wrap-note-error]');

  const setErrorVisible = (isVisible) => {
    if (errorEl) errorEl.classList.toggle('is-hidden', !isVisible);
  };

  const save = (value) => {
    setErrorVisible(false);

    fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [noteAttributeKey]: value } }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        console.error('[gift-wrap] note save failed', error);
        setErrorVisible(true);
      });
  };

  const reset = () => {
    textarea.value = '';
    setErrorVisible(false);
  };

  textarea.addEventListener(
    'change',
    debounce((event) => save(event.target.value), 300)
  );

  return { reset };
};

const initGiftWrapToggle = (toggle) => {
  const checkbox = toggle.querySelector('input[type="checkbox"]');
  const loadingEl = toggle.querySelector('.gift-wrap__loading');
  const noteEl = toggle.querySelector('gift-wrap-note');

  const variantId = toggle.dataset.variantId;
  const mode = toggle.dataset.mode || 'per_item';
  const attributeKey = toggle.dataset.attributeKey;
  const noteAttributeKey = toggle.dataset.noteAttributeKey;
  const qtyInCart = parseInt(toggle.dataset.qtyInCart, 10) || 0;
  const otherItems = parseInt(toggle.dataset.otherItems, 10) || 0;

  const setLoading = (isLoading) => {
    toggle.classList.toggle('is-loading', isLoading);
  };

  const setNoteOpen = (isOpen) => {
    if (noteEl) noteEl.classList.toggle('is-hidden', !isOpen);
  };

  const { reset: resetNote } = initGiftWrapNote(noteEl, noteAttributeKey);

  // Read the live cart rather than the (possibly stale, see file header)
  // otherItems snapshot, so a customer who added/removed items after this
  // element was initialized still gets the correct wrap quantity when
  // they check the box now.
  const currentCartState = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) =>
        cart.items.reduce(
          (state, item) => {
            if (String(item.variant_id) === String(variantId)) {
              state.currentQty = item.quantity;
            } else {
              state.otherItems += item.quantity;
            }
            return state;
          },
          { otherItems: 0, currentQty: 0 }
        )
      );

  const currentOtherItemsCount = () => currentCartState().then((state) => state.otherItems);

  const targetQuantity = (otherItemsCount) => {
    // per_item: one wrap per other line item in the cart.
    // per_order: a single flat wrap regardless of item count.
    return mode === 'per_order' ? 1 : Math.max(otherItemsCount, 1);
  };

  const postUpdate = (body) => {
    fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
        return response.json();
      })
      .then(() => {
        setLoading(false);
        setNoteOpen(checkbox.checked);
        if (!checkbox.checked) resetNote();
        refreshCart();
      })
      .catch((error) => {
        setLoading(false);
        console.error('[gift-wrap] cart update failed', error);
      });
  };

  const remove = () => {
    setLoading(true);
    checkbox.checked = false;
    postUpdate({
      updates: { [variantId]: 0 },
      attributes: { [attributeKey]: '', [noteAttributeKey]: '' },
    });
  };

  const add = () => {
    setLoading(true);
    currentOtherItemsCount()
      .then((otherItemsCount) =>
        postUpdate({
          updates: { [variantId]: targetQuantity(otherItemsCount) },
          attributes: { [attributeKey]: 'yes' },
        })
      )
      .catch((error) => {
        setLoading(false);
        console.error('[gift-wrap] failed to read current cart', error);
      });
  };

  // Reconciles the wrap line's quantity against the current cart. Safe to
  // call after any mutation, including this element's own: postUpdate()
  // triggers refreshCart() -> getCart() -> back into this function, but by
  // then currentQty already matches the target, so it's a no-op and the
  // loop terminates.
  const syncQuantity = () => {
    if (!checkbox.checked) return;

    currentCartState()
      .then(({ otherItems: liveOtherItems, currentQty }) => {
        if (liveOtherItems === 0) {
          remove();
          return;
        }

        const target = targetQuantity(liveOtherItems);
        if (target === currentQty) return;

        postUpdate({ updates: { [variantId]: target } });
      })
      .catch((error) => console.error('[gift-wrap] failed to sync quantity', error));
  };

  // Wrap quantity/presence must track the rest of the cart on every
  // mutation, not just the one that toggled the checkbox — e.g. adding a
  // second product from a different page while wrap is already active, or
  // emptying the cart from elsewhere while wrap stays checked. Applies to
  // both pricing modes: per_order's quantity target is always 1 regardless
  // of otherItems (see targetQuantity), so this only ever acts on it via
  // syncQuantity's empty-cart branch. There's still no pub/sub here (see
  // file header), so this hooks the same signal the rest of the theme
  // already treats as "cart changed": every mutation path in CartDrawer
  // ends by calling `getCart()`. Patched once per page since window.cart
  // is a single shared instance.
  const watchCartUpdates = () => {
    if (!window.cart || typeof window.cart.getCart !== 'function' || window.cart.getCart.giftWrapPatched) {
      return;
    }

    const originalGetCart = window.cart.getCart.bind(window.cart);
    window.cart.getCart = (...args) => {
      const result = originalGetCart(...args);
      syncQuantity();
      return result;
    };
    window.cart.getCart.giftWrapPatched = true;
  };

  checkbox.addEventListener('change', (event) => {
    event.target.checked ? add() : remove();
  });

  // Minimal self-heal: if the cart contains nothing but the wrap item
  // itself, there's nothing left to wrap it around — remove it. Safe to
  // use the mount-time snapshot here since nothing can have changed
  // between the server render and this init pass.
  if (qtyInCart > 0 && otherItems === 0) {
    remove();
  }

  watchCartUpdates();
};

const init = () => {
  document.querySelectorAll('gift-wrap-toggle').forEach(initGiftWrapToggle);
};

document.addEventListener('DOMContentLoaded', init);
